import { Fragment, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  checkBalance,
  JOURNAL_SOURCE_LABELS,
  JOURNAL_STATUS_DEFS,
  JOURNAL_STATUS_MAP,
  type JournalSource,
  type JournalStatus,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money } from '../../lib/format';
import {
  currencySymbol,
  fxMoney,
  isForeignCurrency,
  rateOf,
  rateText,
  toBase,
  useRates,
} from '../../lib/currency';
import { Icon } from '../../components/Icon';
import { ExportMenu } from '../../components/ExportMenu';
import {
  exportDay,
  exportNotes,
  exportNum,
  exportPeriod,
  EXPORT_ROW_LIMIT,
  fetchAllRows,
  type TableExportDoc,
} from '../../lib/tableExport';
import {
  describeFilters,
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import { AccountPicker } from '../../components/RecordPicker';
import { LedgerHistory } from '../../components/LedgerHistory';
import { Code, CodeText } from '../../components/Code';
import {
  AttachmentCountButton,
  Attachments,
  PendingFiles,
  uploadAttachments,
  useAttachmentCounts,
} from '../../components/Attachments';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
} from '../../components/ui';

interface EntryLine {
  id: string;
  accountId: string;
  debit: string;
  credit: string;
  description: string | null;
  partnerAccountId: string | null;
  partnerSupplierId: string | null;
  /** اسما الطرف كما يحسمهما الخادم — في القائمة والتفاصيل معًا. */
  partnerAccountName?: string | null;
  partnerSupplierName?: string | null;
  /** لبند على حساب بعملة أجنبية: المبلغ بعملته (بإشارة) وسعره. */
  currency?: string | null;
  fxAmount?: string | null;
  fxRate?: string | null;
  account: { id: string; code: string; name: string; currency?: string };
}

interface Entry {
  id: string;
  number: string;
  date: string;
  description: string;
  reference: string | null;
  source: JournalSource;
  sourceLabel: string;
  status: JournalStatus;
  totalDebit: number;
  /** سبب منع التعديل من الخادم، أو null إن جاز. */
  lockReason: string | null;
  /** سبب منع الإلغاء من الخادم، أو null إن جاز. */
  voidLockReason?: string | null;
  /** سبب إلغاء القيد الملغى — من سجله. */
  voidNote?: string | null;
  reversalOf?: { id: string; number: string } | null;
  reversedBy?: { id: string; number: string } | null;
  lines: EntryLine[];
  /** أطراف القيد من بنوده بلا تكرار — عمود «العميل / المورد». */
  parties?: Party[];
}

/** عميل أو مورد على أحد بنود القيد. */
interface Party {
  kind: 'customer' | 'supplier';
  id: string;
  name: string;
}

interface EntryPage {
  items: Entry[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  /** إجمالي قيم كل القيود المطابقة للشروط، عبر الصفحات كلها. */
  totalAmount: number;
}

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في journal.service.ts. */
const JOURNAL_FILTERS: FilterDef[] = [
  { key: 'number', label: 'الرقم', kind: 'text', sortable: true },
  { key: 'date', label: 'التاريخ', kind: 'date', sortable: true },
  { key: 'description', label: 'البيان', kind: 'text', noList: true },
  { key: 'party', label: 'العميل / المورد', kind: 'text' },
  { key: 'reference', label: 'المرجع', kind: 'text' },
  {
    key: 'source',
    label: 'المصدر',
    kind: 'enum',
    options: Object.entries(JOURNAL_SOURCE_LABELS).map(([value, label]) => ({ value, label })),
  },
  { key: 'amount', label: 'القيمة', kind: 'number' },
  {
    key: 'status',
    label: 'الحالة',
    kind: 'enum',
    options: JOURNAL_STATUS_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
  /* لا عمود له في الجدول: الحساب في البنود، ويُفلتر به من زرّ «فلتر». */
  { key: 'account', label: 'الحساب', kind: 'text' },
];

const COLS = 10;

/** أسماء أطراف القيد نصًّا واحدًا — للتصدير. */
const partyText = (parties: Party[] | undefined) =>
  parties?.length ? parties.map((p) => p.name).join('، ') : null;

/**
 * طرفٌ رابطًا إلى صفحته: العميل إلى ملفه، والمورد إلى قائمة الموردين
 * مبحوثًا فيها باسمه — لا صفحة مستقلة للمورد بعد.
 */
function PartyLink({ party }: { party: Party }) {
  return party.kind === 'customer' ? (
    <Link to={`/crm/accounts/${party.id}`} title="ملف العميل">
      {party.name}
    </Link>
  ) : (
    <Link to={`/finance/suppliers?q=${encodeURIComponent(party.name)}`} title="المورد">
      {party.name}
      <span className="tiny mute"> (مورد)</span>
    </Link>
  );
}

/** أطراف القيد مفصولة بفواصل، أو «—» إن لم يكن له طرف. */
function PartyList({ parties }: { parties: Party[] | undefined }) {
  if (!parties?.length) return <span className="cell-mute">—</span>;
  return (
    <>
      {parties.map((p, i) => (
        <Fragment key={`${p.kind}:${p.id}`}>
          {i > 0 && '، '}
          <PartyLink party={p} />
        </Fragment>
      ))}
    </>
  );
}

/**
 * خلية البيان: النصّ كاملًا على أسطر، لا مقطوعًا.
 *
 * البيان هو ما يشرح القيد، والمحاسب يقرؤه كلّه عند المراجعة. فيلتفّ على
 * أسطر بعرضٍ أدنى يُبقيه مقروءًا، وأقصى يمنعه من دفع الأرقام خارج الشاشة.
 * و`overflow-wrap` يكسر كلمةً طويلة بلا مسافات (رقم حوالة، رابط) بدل أن تفيض.
 */
const DESC_CELL: CSSProperties = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  minWidth: 220,
  maxWidth: 480,
};

/** مبلغ القيد الملغى: مشطوب باهت — ظاهرٌ للمراجعة وخارجٌ من الحساب. */
const VOID_AMOUNT: CSSProperties = {
  textDecoration: 'line-through',
  color: 'var(--ink-3)',
};

export function JournalList() {
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [reversing, setReversing] = useState<Entry | null>(null);
  const [voiding, setVoiding] = useState<Entry | null>(null);
  const [filesFor, setFilesFor] = useState<Entry | null>(null);

  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['journal', query],
    queryFn: () => api<EntryPage>(`/accounting/journal${qs(query)}`),
  });

  /* عدد سندات القيود لصفوف الصفحة كلها في طلب واحد. */
  const fileCounts = useAttachmentCounts(
    'journalEntry',
    (list.data?.items ?? []).map((e) => e.id),
  );

  const post = useMutation({
    mutationFn: (id: string) => api(`/accounting/journal/${id}/post`, { method: 'POST' }),
    onSuccess: () => refresh(),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['journal'] });
    void queryClient.invalidateQueries({ queryKey: ['journal-entry'] });
    void queryClient.invalidateQueries({ queryKey: ['journal-history'] });
    void queryClient.invalidateQueries({ queryKey: ['chart'] });
    void queryClient.invalidateQueries({ queryKey: ['trial-balance'] });
    /*
     * كل ما يقرأ الأرصدة يتقادم: كشوف العملاء والموردين والخزائن والقوائم.
     * القيد الملغى أو المعكوس يغيّرها فورًا، ولا يصحّ أن تُعرض نصف دقيقة
     * من الذاكرة بأرقام ما قبل التغيير.
     */
    for (const key of [
      'party-statement',
      'statement-party',
      'statement-detail',
      'cash-accounts',
      'cash-statement',
      'balances',
      'balance-sheet',
      'income-statement',
      'ledger',
      'reports',
      'suppliers',
    ]) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
    setReversing(null);
    setVoiding(null);
    setCreating(false);
    setEditing(null);
  }

  const ranged = Boolean(state.from || state.to);

  /*
   * التصدير: كل القيود المطابقة للبحث والحالة والمصدر والمدى والفلاتر، لا
   * الصفحة المعروضة وحدها. نطاقان: قيدٌ في كل صف كالجدول، أو بنودها —
   * والقائمة تجلب البنود أصلًا، فلا طلب زائد.
   */
  const buildExport = async (withLines: boolean): Promise<TableExportDoc> => {
    const { items, total, truncated } = await fetchAllRows<Entry>('/accounting/journal', query, 100);
    const status = (s: JournalStatus) => JOURNAL_STATUS_MAP[s]?.label ?? s;
    const notes = exportNotes({
      q: debounced,
      conditions: [
        state.status && `الحالة: ${status(state.status as JournalStatus)}`,
        state.source &&
          `المصدر: ${JOURNAL_SOURCE_LABELS[state.source as JournalSource] ?? state.source}`,
        ...describeFilters(JOURNAL_FILTERS, filters),
      ],
      count: items.length,
      total,
      truncated,
      unit: 'القيود',
    });
    const period = exportPeriod(state.from, state.to);

    if (!withLines) {
      return {
        title: 'القيود اليومية',
        sheets: [
          {
            title: 'القيود اليومية',
            period,
            sections: [
              {
                columns: [
                  /* رقمُ القيد والمصدرُ والحالةُ سطرٌ واحد؛ والبيانُ يأخذ باقي العرض. */
                  { key: 'number', label: 'الرقم', kind: 'code' },
                  { key: 'date', label: 'التاريخ', kind: 'date' },
                  { key: 'description', label: 'البيان', minWidth: 28 },
                  { key: 'party', label: 'العميل / المورد', minWidth: 14 },
                  { key: 'reference', label: 'المرجع' },
                  { key: 'source', label: 'المصدر', wrap: false },
                  { key: 'amount', label: 'القيمة', kind: 'money', total: true },
                  { key: 'status', label: 'الحالة', wrap: false },
                ],
                rows: items.map((e) => ({
                  number: e.number,
                  date: exportDay(e.date),
                  description: e.description,
                  party: partyText(e.parties),
                  reference: e.reference,
                  source: e.sourceLabel,
                  // الملغى بلا قيمة: إجمالي التصدير يطابق إجمالي الصفحة والأرصدة.
                  amount: e.status === 'VOID' ? null : e.totalDebit,
                  status: status(e.status),
                })),
              },
            ],
            notes,
          },
        ],
      };
    }

    /* بندٌ في كل صف — والقسم الواحد لا يتجاوز سقف الصفوف. */
    const lines = items
      .flatMap((e) =>
        e.lines.map((l) => ({
          number: e.number,
          date: exportDay(e.date),
          description: e.description,
          account: `${l.account.code} ${l.account.name}`,
          line: l.description,
          // طرف البند نفسه لا أطراف القيد كلها.
          party: l.partnerAccountName ?? l.partnerSupplierName ?? null,
          debit: e.status === 'VOID' ? null : exportNum(l.debit) || null,
          credit: e.status === 'VOID' ? null : exportNum(l.credit) || null,
          status: status(e.status),
        })),
      );
    const rows = lines.slice(0, EXPORT_ROW_LIMIT);
    const lineNote =
      rows.length < lines.length
        ? `صُدِّر أوّل ${rows.length} بندًا من ${lines.length} — ضيّق المدى أو الفلاتر لتصدير الباقي.`
        : `عدد البنود: ${rows.length}`;
    return {
      title: 'القيود اليومية — البنود',
      sheets: [
        {
          title: 'بنود القيود',
          period,
          sections: [
            {
              columns: [
                { key: 'number', label: 'رقم القيد', kind: 'code' },
                { key: 'date', label: 'التاريخ', kind: 'date' },
                { key: 'description', label: 'بيان القيد', minWidth: 22 },
                { key: 'account', label: 'الحساب', minWidth: 16 },
                { key: 'line', label: 'بيان البند', minWidth: 16 },
                { key: 'party', label: 'العميل / المورد', minWidth: 12 },
                { key: 'debit', label: 'مدين', kind: 'money', total: true },
                { key: 'credit', label: 'دائن', kind: 'money', total: true },
                { key: 'status', label: 'الحالة', wrap: false },
              ],
              rows,
            },
          ],
          notes: [...notes.slice(0, 19), lineNote],
        },
      ],
    };
  };

  return (
    <div className="page">
      <PageHead
        title="القيود اليومية"
        subtitle="كل حركة مالية في الدفتر العام"
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={15} />
            قيد يدوي
          </button>
        }
      />

      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث برقم القيد أو بيانه أو مرجعه أو اسم العميل"
        />

        <select
          className="select"
          style={{ width: 'auto' }}
          value={state.status ?? ''}
          onChange={(e) => patch({ status: e.target.value || undefined })}
          aria-label="الحالة"
        >
          <option value="">كل الحالات</option>
          {JOURNAL_STATUS_DEFS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          className="select"
          style={{ width: 'auto' }}
          value={state.source ?? ''}
          onChange={(e) => patch({ source: e.target.value || undefined })}
          aria-label="المصدر"
        >
          <option value="">كل المصادر</option>
          {Object.entries(JOURNAL_SOURCE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        {/*
          المدى على تاريخ القيد في الخادم، و«إلى» يشمل يومه كله. ويبقى في
          الرابط فيُحفظ مع التنقّل، ويحكم القائمة وقيم الأعمدة والإجمالي معًا.
        */}
        <label className="row" style={{ gap: 'var(--sp-1)' }}>
          <span className="tiny mute">من تاريخ</span>
          <input
            className="input"
            type="date"
            style={{ width: 'auto' }}
            value={state.from ?? ''}
            max={state.to || undefined}
            onChange={(e) => patch({ from: e.target.value || undefined })}
            aria-label="من تاريخ"
          />
        </label>
        <label className="row" style={{ gap: 'var(--sp-1)' }}>
          <span className="tiny mute">إلى تاريخ</span>
          <input
            className="input"
            type="date"
            style={{ width: 'auto' }}
            value={state.to ?? ''}
            min={state.from || undefined}
            onChange={(e) => patch({ to: e.target.value || undefined })}
            aria-label="إلى تاريخ"
          />
        </label>
        {ranged && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => patch({ from: undefined, to: undefined })}
          >
            <Icon name="x" size={13} />
            كل التواريخ
          </button>
        )}

        <ExportMenu
          disabled={!list.data?.total}
          scopes={[
            { key: 'entries', label: 'القيود', build: () => buildExport(false) },
            {
              key: 'lines',
              label: 'بالبنود',
              hint: 'بندٌ في كل صف: الحساب ومدينه ودائنه',
              build: () => buildExport(true),
            },
          ]}
        />

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={JOURNAL_FILTERS} filters={filters} onChange={setFilters} />
        </span>
      </div>

      <FilterChips defs={JOURNAL_FILTERS} filters={filters} onChange={setFilters} />

      <FilterScope
        defs={JOURNAL_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/accounting/journal/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
        <div className="card">
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 40 }} />
                  <th>
                    الرقم
                    <HeaderFilter colKey="number" />
                  </th>
                  <th>
                    التاريخ
                    <HeaderFilter colKey="date" />
                  </th>
                  <th style={{ minWidth: 220 }}>
                    البيان
                    <HeaderFilter colKey="description" />
                  </th>
                  <th style={{ minWidth: 140 }}>
                    العميل / المورد
                    <HeaderFilter colKey="party" />
                  </th>
                  <th>
                    المرجع
                    <HeaderFilter colKey="reference" />
                  </th>
                  <th>
                    المصدر
                    <HeaderFilter colKey="source" />
                  </th>
                  <th className="col-num">
                    القيمة
                    <HeaderFilter colKey="amount" />
                  </th>
                  <th>
                    الحالة
                    <HeaderFilter colKey="status" />
                  </th>
                  <th />
                </tr>
              </thead>

              {list.isPending && <TableSkeleton cols={COLS} />}

              {list.data && (
                <tbody>
                  {list.data.items.map((entry) => (
                    <Fragment key={entry.id}>
                      <tr>
                        <td>
                          <button
                            className="btn btn-icon btn-ghost btn-sm"
                            onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                            aria-label={expanded === entry.id ? 'طي البنود' : 'عرض البنود'}
                            aria-expanded={expanded === entry.id}
                          >
                            <Icon
                              name={expanded === entry.id ? 'chevronDown' : 'chevronLeft'}
                              size={14}
                            />
                          </button>
                        </td>
                        <td className="num cell-strong">
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() => setViewing(entry.id)}
                            title="تفاصيل القيد وسجل تعديلاته"
                          >
                            <Code>{entry.number}</Code>
                          </button>
                        </td>
                        <td className="cell-mute date">{date(entry.date)}</td>
                        <td style={DESC_CELL}>
                          <CodeText text={entry.description} />
                        </td>
                        <td>
                          <PartyList parties={entry.parties} />
                        </td>
                        <td className="cell-mute">
                          {/* المرجعُ نصٌّ حرّ: يأخذ اتجاهَ أوّل حرفٍ فيه (رقمُ شيكٍ لاتينيّ أو ملاحظةٌ عربيّة). */}
                          <bdi>{entry.reference ?? '—'}</bdi>
                        </td>
                        <td className="cell-mute">{entry.sourceLabel}</td>
                        <td
                          className="num"
                          style={entry.status === 'VOID' ? VOID_AMOUNT : undefined}
                          title={entry.status === 'VOID' ? 'ملغى — خارج الأرصدة والإجمالي' : undefined}
                        >
                          {money(entry.totalDebit)}
                        </td>
                        <td>
                          <span
                            title={
                              entry.status === 'VOID' && entry.voidNote
                                ? `سبب الإلغاء: ${entry.voidNote}`
                                : undefined
                            }
                          >
                            <Badge kind={JOURNAL_STATUS_MAP[entry.status]?.kind}>
                              {JOURNAL_STATUS_MAP[entry.status]?.label}
                            </Badge>
                          </span>
                        </td>
                        <td className="end nowrap">
                          <AttachmentCountButton
                            count={fileCounts[entry.id] ?? 0}
                            onClick={() => setFilesFor(entry)}
                            label={`سند القيد ${entry.number}`}
                          />
                          <button
                            className="btn btn-sm btn-icon btn-ghost"
                            onClick={() => setViewing(entry.id)}
                            title="التفاصيل وسجل التعديلات"
                            aria-label="التفاصيل وسجل التعديلات"
                          >
                            <Icon name="clock" size={14} />
                          </button>
                          <button
                            className="btn btn-sm btn-icon btn-ghost"
                            onClick={() => setEditing(entry.id)}
                            disabled={!!entry.lockReason}
                            title={entry.lockReason ?? 'تعديل القيد'}
                            aria-label="تعديل القيد"
                          >
                            <Icon name="edit" size={14} />
                          </button>
                          {entry.status === 'DRAFT' && (
                            <button
                              className="btn btn-sm"
                              onClick={() => post.mutate(entry.id)}
                              disabled={post.isPending}
                            >
                              ترحيل
                            </button>
                          )}
                          {entry.status === 'POSTED' && !entry.reversedBy && (
                            <button className="btn btn-sm" onClick={() => setReversing(entry)}>
                              عكس
                            </button>
                          )}
                          {entry.status === 'POSTED' && (
                            <button
                              className="btn btn-sm"
                              onClick={() => setVoiding(entry)}
                              disabled={!!entry.voidLockReason}
                              title={
                                entry.voidLockReason ??
                                'إلغاء القيد: يخرج من الحسابات والتقارير ويبقى ظاهرًا بحالة «ملغى»'
                              }
                            >
                              إلغاء
                            </button>
                          )}
                        </td>
                      </tr>

                      {expanded === entry.id && (
                        <tr>
                          <td colSpan={COLS} style={{ background: 'var(--surface-2)', padding: 0 }}>
                            <LinesTable lines={entry.lines} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              )}
            </table>
          </div>

          {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}
          {post.isError && (
            <div className="alert alert-danger" style={{ margin: 'var(--sp-4)' }}>
              {post.error instanceof Error ? post.error.message : 'تعذّر الترحيل.'}
            </div>
          )}

          {list.data?.items.length === 0 && (
            <EmptyState
              title="لا توجد قيود"
              text="تُنشأ القيود تلقائيًا عند إرسال الفواتير وتسجيل التحصيل، أو يدويًا للتسويات."
            />
          )}

          {list.data && list.data.total > 0 && (
            <div className="totals-row" style={{ padding: 'var(--sp-2) var(--sp-4)' }}>
              <span className="small mute">
                إجمالي قيم القيود المعروضة (كل الصفحات، بلا الملغاة)
              </span>
              <span className="num strong">{money(list.data.totalAmount)}</span>
            </div>
          )}

          {list.data && (
            <Pager
              page={list.data.page}
              pageCount={list.data.pageCount}
              total={list.data.total}
              pageSize={list.data.pageSize}
              onPage={(p) => patch({ page: String(p) })}
              onPageSize={(n) => patch({ pageSize: String(n), page: undefined })}
            />
          )}
        </div>
      </FilterScope>

      {creating && <JournalModal onClose={() => setCreating(false)} onSaved={refresh} />}

      {editing && (
        <EditJournalLoader id={editing} onClose={() => setEditing(null)} onSaved={refresh} />
      )}

      {viewing && (
        <EntryDetail
          id={viewing}
          onClose={() => setViewing(null)}
          onEdit={() => {
            setEditing(viewing);
            setViewing(null);
          }}
          onReverse={(e) => {
            setReversing(e);
            setViewing(null);
          }}
          onVoid={(e) => {
            setVoiding(e);
            setViewing(null);
          }}
        />
      )}

      {reversing && (
        <ReverseModal entry={reversing} onClose={() => setReversing(null)} onDone={refresh} />
      )}

      {voiding && (
        <VoidModal entry={voiding} onClose={() => setVoiding(null)} onDone={refresh} />
      )}

      {/* سند القيد من صفّه مباشرةً — بلا فتح التفاصيل. */}
      {filesFor && (
        <Modal title={`سند القيد ${filesFor.number}`} onClose={() => setFilesFor(null)}>
          <Attachments entity="journalEntry" entityId={filesFor.id} title="المرفقات" />
        </Modal>
      )}
    </div>
  );
}

function LinesTable({ lines }: { lines: EntryLine[] }) {
  return (
    <table className="table" style={{ margin: 0 }}>
      <thead>
        <tr>
          <th style={{ paddingInlineStart: 56 }}>الحساب</th>
          <th>البيان</th>
          <th>الطرف</th>
          <th style={{ width: 140 }}>مدين</th>
          <th style={{ width: 140 }}>دائن</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={line.id}>
            <td style={{ paddingInlineStart: 56 }}>
              <span className="num cell-mute">{line.account.code}</span> {line.account.name}
            </td>
            <td className="cell-mute" style={DESC_CELL}>
              <CodeText text={line.description ?? '—'} />
            </td>
            <td className="cell-mute">
              {line.partnerAccountName ??
                line.partnerSupplierName ??
                (line.partnerAccountId || line.partnerSupplierId ? '•' : '—')}
            </td>
            <td className="num">
              {Number(line.debit) ? money(line.debit) : ''}
              {Number(line.debit) && line.fxAmount != null ? <FxNote line={line} /> : null}
            </td>
            <td className="num">
              {Number(line.credit) ? money(line.credit) : ''}
              {Number(line.credit) && line.fxAmount != null ? <FxNote line={line} /> : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** المبلغ بعملة البند الأجنبية وسعره تحت مبلغه بالجنيه. */
function FxNote({ line }: { line: EntryLine }) {
  return (
    <div className="tiny mute">
      {fxMoney(Math.abs(Number(line.fxAmount)), line.currency)} × {rateText(line.fxRate)}
    </div>
  );
}

/* ==========================================================================
   تفاصيل القيد وسجل تعديلاته
   ========================================================================== */

function EntryDetail({
  id,
  onClose,
  onEdit,
  onReverse,
  onVoid,
}: {
  id: string;
  onClose: () => void;
  onEdit: () => void;
  onReverse: (entry: Entry) => void;
  onVoid: (entry: Entry) => void;
}) {
  const entry = useQuery({
    queryKey: ['journal-entry', id],
    queryFn: () => api<Entry>(`/accounting/journal/${id}`),
  });
  const e = entry.data;

  return (
    <Modal
      title={e ? `القيد ${e.number}` : 'القيد'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إغلاق
          </button>
          {e?.status === 'POSTED' && !e.reversedBy && (
            <button className="btn" onClick={() => onReverse(e)} type="button">
              <Icon name="refresh" size={14} />
              عكس
            </button>
          )}
          {e?.status === 'POSTED' && (
            <button
              className="btn"
              onClick={() => onVoid(e)}
              type="button"
              disabled={!!e.voidLockReason}
              title={
                e.voidLockReason ??
                'إلغاء القيد: يخرج من الحسابات والتقارير ويبقى ظاهرًا بحالة «ملغى»'
              }
            >
              <Icon name="x" size={14} />
              إلغاء
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={onEdit}
            disabled={!e || !!e.lockReason}
            title={e?.lockReason ?? undefined}
          >
            <Icon name="edit" size={14} />
            تعديل
          </button>
        </>
      }
    >
      {entry.isPending && <div className="skeleton" style={{ height: 160 }} />}
      {entry.isError && <ErrorState error={entry.error} onRetry={() => void entry.refetch()} />}

      {e && (
        <>
          <div className="form-grid mb-4">
            {/* الطرف أولًا: أول ما يُسأل عنه في القيد هو «لمن؟». */}
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="tiny mute">العميل / المورد</div>
              <div className="strong">
                <PartyList parties={e.parties} />
              </div>
            </div>
            <div>
              <div className="tiny mute">التاريخ</div>
              <div>{date(e.date)}</div>
            </div>
            {/* البيان على عرض النموذج كلّه — نصٌّ طويل لا يُحشر في عمود. */}
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="tiny mute">البيان</div>
              <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{e.description}</div>
            </div>
            <div>
              <div className="tiny mute">المرجع</div>
              <div dir="ltr">{e.reference ?? '—'}</div>
            </div>
            <div>
              <div className="tiny mute">المصدر والحالة</div>
              <div className="row" style={{ gap: 'var(--sp-2)' }}>
                <span>{e.sourceLabel}</span>
                <Badge kind={JOURNAL_STATUS_MAP[e.status]?.kind}>
                  {JOURNAL_STATUS_MAP[e.status]?.label}
                </Badge>
              </div>
            </div>
          </div>

          {(e.reversalOf || e.reversedBy) && (
            <p className="small mute mb-4">
              {e.reversalOf && `عاكس للقيد ${e.reversalOf.number}. `}
              {e.reversedBy && `عُكس بالقيد ${e.reversedBy.number}.`}
            </p>
          )}

          {e.status === 'VOID' ? (
            <div className="alert alert-danger mb-4">
              <Icon name="alert" size={18} />
              <div>
                القيد ملغى — خارج الحسابات والتقارير وكشوف الحساب، ويبقى هنا للمراجعة.
                {e.voidNote && <div className="strong">السبب: {e.voidNote}</div>}
              </div>
            </div>
          ) : (
            e.lockReason && (
              <div className="alert alert-info mb-4">
                <Icon name="info" size={18} />
                <div>{e.lockReason}</div>
              </div>
            )
          )}

          <div
            className="table-wrap mb-4"
            style={e.status === 'VOID' ? { opacity: 0.55, textDecoration: 'line-through' } : undefined}
          >
            <LinesTable lines={e.lines} />
          </div>

          {/* سند القيد: إيصال أو صورة تحويل — يُرفع ويُعاين ويُحذف من هنا. */}
          <div className="mb-4">
            <Attachments entity="journalEntry" entityId={id} title="المرفقات" />
          </div>

          <h3 className="card-title mb-4">سجل التعديلات</h3>
          <LedgerHistory
            path={`/accounting/journal/${id}/history`}
            queryKey={['journal-history', id]}
          />
        </>
      )}
    </Modal>
  );
}

/* ==========================================================================
   قيد يدوي — إنشاء وتعديل
   ========================================================================== */

interface PostableAccount {
  id: string;
  code: string;
  name: string;
  type: string;
  currency?: string;
}

interface DraftLine {
  accountId: string;
  debit: string;
  credit: string;
  description: string;
  partnerAccountId: string | null;
  partnerName: string | null;
  /** المورد لا يُحرَّر هنا، لكنه يُحفظ كما كان فلا يضيع بالتعديل. */
  partnerSupplierId: string | null;
  /** حساب البند قبل التعديل — يُعرض وإن عُطّل فخرج من قائمة الاختيار. */
  accountLabel?: string;
  /**
   * سعر الصرف لبند على حساب بعملة أجنبية. المدين والدائن في البند حينها
   * بعملة الحساب، ومقابلهما بالجنيه حاصل الضرب في السعر.
   */
  fxRate: string;
  accountCurrency?: string;
}

const emptyLine = (): DraftLine => ({
  accountId: '',
  debit: '',
  credit: '',
  description: '',
  partnerAccountId: null,
  partnerName: null,
  partnerSupplierId: null,
  fxRate: '',
});

/** يجلب القيد كاملًا (بأسماء أطرافه) ثم يفتح نموذج التعديل. */
function EditJournalLoader({
  id,
  onClose,
  onSaved,
}: {
  id: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const entry = useQuery({
    queryKey: ['journal-entry', id],
    queryFn: () => api<Entry>(`/accounting/journal/${id}`),
  });

  if (!entry.data) {
    return (
      <Modal title="تعديل القيد" onClose={onClose}>
        {entry.isError ? (
          <ErrorState error={entry.error} onRetry={() => void entry.refetch()} />
        ) : (
          <div className="skeleton" style={{ height: 160 }} />
        )}
      </Modal>
    );
  }
  return <JournalModal entry={entry.data} onClose={onClose} onSaved={onSaved} />;
}

function JournalModal({
  entry,
  onClose,
  onSaved,
}: {
  /** حين يُمرَّر يصير النموذج تعديلًا لهذا القيد. */
  entry?: Entry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = Boolean(entry);
  const [entryDate, setEntryDate] = useState(
    entry ? entry.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [description, setDescription] = useState(entry?.description ?? '');
  const [reference, setReference] = useState(entry?.reference ?? '');
  const [lines, setLines] = useState<DraftLine[]>(
    entry
      ? entry.lines.map((l) => ({
          accountId: l.accountId,
          // البند الأجنبي يُفتح بمبلغه بعملته، والسعر بجواره.
          debit: Number(l.debit)
            ? String(l.fxAmount != null ? Math.abs(Number(l.fxAmount)) : Number(l.debit))
            : '',
          credit: Number(l.credit)
            ? String(l.fxAmount != null ? Math.abs(Number(l.fxAmount)) : Number(l.credit))
            : '',
          fxRate: l.fxRate != null ? String(Number(l.fxRate)) : '',
          accountCurrency: l.fxAmount != null ? (l.currency ?? undefined) : undefined,
          description: l.description ?? '',
          partnerAccountId: l.partnerAccountId,
          partnerName: l.partnerAccountName ?? null,
          partnerSupplierId: l.partnerSupplierId,
          accountLabel: `${l.account.code} — ${l.account.name}`,
        }))
      : [emptyLine(), emptyLine()],
  );
  const [postNow, setPostNow] = useState(true);
  const [pickingPartner, setPickingPartner] = useState<number | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  /* القيد حُفظ وتعثّر رفع سنده — يُعرض لإعادة الرفع بدل إعادة الحفظ فيتكرّر القيد. */
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [savedEntry, setSavedEntry] = useState<{ id: string; number: string } | null>(null);

  const accounts = useQuery({
    queryKey: ['accounts', 'postable'],
    queryFn: () => api<PostableAccount[]>('/accounting/accounts/postable'),
  });
  const rates = useRates(entryDate);

  /** عملة حساب البند — من الدليل، أو مما حُفظ عليه إن خرج من القائمة. */
  const currencyOf = (l: DraftLine) =>
    accounts.data?.find((a) => a.id === l.accountId)?.currency ?? l.accountCurrency ?? 'EGP';

  const payload = lines
    .filter((l) => l.accountId && (Number(l.debit) > 0 || Number(l.credit) > 0))
    .map((l) => {
      const cur = currencyOf(l);
      const rate = Number(l.fxRate) || 0;
      const foreign = isForeignCurrency(cur);
      const fxValue = Number(l.debit) || Number(l.credit) || 0;
      return {
        accountId: l.accountId,
        debit: foreign ? (Number(l.debit) ? toBase(Number(l.debit), rate) : 0) : Number(l.debit) || 0,
        credit: foreign
          ? Number(l.credit)
            ? toBase(Number(l.credit), rate)
            : 0
          : Number(l.credit) || 0,
        description: l.description.trim() || null,
        partnerAccountId: l.partnerAccountId,
        partnerSupplierId: l.partnerSupplierId,
        ...(foreign ? { fxAmount: fxValue, fxRate: rate } : {}),
      };
    });
  const missingRate = lines.some(
    (l) =>
      l.accountId &&
      isForeignCurrency(currencyOf(l)) &&
      (Number(l.debit) > 0 || Number(l.credit) > 0) &&
      !(Number(l.fxRate) > 0),
  );

  // التوازن يُفحص بالدالة نفسها التي يستعملها الخادم، فلا يختلف الحكم بينهما.
  const balance = checkBalance(payload);

  const save = useMutation({
    mutationFn: async (): Promise<string | null> => {
      if (entry) {
        await api(`/accounting/journal/${entry.id}`, {
          method: 'PATCH',
          body: {
            date: new Date(entryDate).toISOString(),
            description: description.trim(),
            reference: reference.trim() || null,
            lines: payload,
          },
        });
        return null;
      }
      const created = await api<{ id: string; number: string }>('/accounting/journal', {
        method: 'POST',
        body: {
          date: new Date(entryDate).toISOString(),
          description: description.trim(),
          reference: reference || undefined,
          lines: payload,
          postImmediately: postNow,
        },
      });
      /* القيد أولًا ثم سنده عليه — وتعثّر الرفع لا يُبطل القيد. */
      if (!files.length) return null;
      const error = await uploadAttachments('journalEntry', created.id, files);
      if (error) setSavedEntry({ id: created.id, number: created.number });
      return error;
    },
    onSuccess: (error) => (error ? setUploadError(error) : onSaved()),
  });

  const patch = (index: number, changes: Partial<DraftLine>) =>
    setLines(lines.map((l, i) => (i === index ? { ...l, ...changes } : l)));

  const canSave =
    balance.balanced && !missingRate && description.trim().length >= 2 && !save.isPending;

  if (savedEntry && uploadError) {
    return (
      <Modal
        title={`القيد ${savedEntry.number}`}
        onClose={onSaved}
        footer={
          <button className="btn btn-primary" onClick={onSaved}>
            تم
          </button>
        }
      >
        <div className="alert alert-warning mb-4">
          <Icon name="alert" size={18} />
          <div>
            حُفظ القيد {savedEntry.number}، لكن تعذّر رفع بعض الملفات: {uploadError} أعد رفعها من
            هنا.
          </div>
        </div>
        <Attachments entity="journalEntry" entityId={savedEntry.id} title="سند القيد" />
      </Modal>
    );
  }

  return (
    <Modal
      title={entry ? `تعديل القيد ${entry.number}` : 'قيد يدوي'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={!canSave}>
            {save.isPending
              ? 'جارٍ الحفظ'
              : editing
                ? 'حفظ التعديل'
                : postNow
                  ? 'حفظ وترحيل'
                  : 'حفظ كمسودة'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {entry?.status === 'POSTED' && (
        <div className="alert alert-warning mb-4">
          <Icon name="alert" size={18} />
          <div>
            القيد مرحَّل: التعديل يغيّر أرصدة الحسابات فور حفظه، ويُسجَّل في سجل التعديلات باسمك
            ووقته مع القيم قبل التعديل وبعده.
          </div>
        </div>
      )}

      <div className="form-grid mb-4">
        <div className="field">
          <label className="label req" htmlFor="j-date">
            التاريخ
          </label>
          <input
            id="j-date"
            className="input"
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label req" htmlFor="j-desc">
            البيان
          </label>
          <input
            id="j-desc"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="تسوية رصيد، إثبات مصروف، رصيد افتتاحي..."
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="j-ref">
            المرجع
          </label>
          <input
            id="j-ref"
            className="input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            dir="ltr"
          />
        </div>
      </div>

      <div className="table-wrap">
        <table className="table lines-table">
          <thead>
            <tr>
              <th style={{ width: '32%' }}>الحساب</th>
              <th>البيان</th>
              <th style={{ width: 170 }}>العميل</th>
              <th style={{ width: 120 }}>مدين</th>
              <th style={{ width: 120 }}>دائن</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              /*
               * حساب البند القديم قد لا يكون في قائمة الحسابات القابلة للترحيل
               * (عُطّل بعد الإدخال)، فيُضاف خيارًا كي لا يظهر البند فارغًا.
               */
              const listed = accounts.data?.some((a) => a.id === line.accountId);
              return (
                <tr key={index}>
                  <td>
                    <select
                      className="select"
                      value={line.accountId}
                      onChange={(e) => {
                        // حساب بعملة أجنبية: يُملأ سعر يوم القيد ويبقى قابلًا للتعديل.
                        const cur = accounts.data?.find((a) => a.id === e.target.value)?.currency;
                        const r = rateOf(rates.data, cur);
                        patch(index, {
                          accountId: e.target.value,
                          accountCurrency: cur,
                          fxRate: isForeignCurrency(cur) && r ? String(r) : '',
                        });
                      }}
                      aria-label="الحساب"
                    >
                      <option value="">اختر حسابًا</option>
                      {line.accountId && !listed && line.accountLabel && (
                        <option value={line.accountId}>{line.accountLabel} (معطّل)</option>
                      )}
                      {accounts.data?.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                          {isForeignCurrency(a.currency) ? ` (${a.currency})` : ''}
                        </option>
                      ))}
                    </select>
                    {isForeignCurrency(currencyOf(line)) && (
                      <div className="row mt-2" style={{ gap: 6 }}>
                        <span className="tiny mute">
                          المبلغ بـ {currencySymbol(currencyOf(line))} — السعر
                        </span>
                        <input
                          className="input num"
                          style={{ maxWidth: 110 }}
                          type="number"
                          min="0"
                          step="any"
                          value={line.fxRate}
                          onChange={(e) => patch(index, { fxRate: e.target.value })}
                          aria-label="سعر الصرف"
                        />
                        <span className="tiny mute num">
                          {Number(line.fxRate) > 0 && (Number(line.debit) || Number(line.credit))
                            ? `= ${money(
                                toBase(Number(line.debit) || Number(line.credit), Number(line.fxRate)),
                              )}`
                            : ''}
                        </span>
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      className="input"
                      value={line.description}
                      onChange={(e) => patch(index, { description: e.target.value })}
                      aria-label="بيان البند"
                    />
                  </td>
                  <td>
                    {line.partnerAccountId ? (
                      <span className="row" style={{ gap: 4 }}>
                        <span
                          className="small"
                          title={line.partnerName ?? ''}
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 120,
                          }}
                        >
                          {line.partnerName ?? 'عميل'}
                        </span>
                        <button
                          type="button"
                          className="btn btn-icon btn-ghost btn-sm"
                          onClick={() =>
                            patch(index, { partnerAccountId: null, partnerName: null })
                          }
                          aria-label="إزالة العميل"
                        >
                          <Icon name="x" size={13} />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => setPickingPartner(index)}
                      >
                        <Icon name="user" size={13} />
                        ربط عميل
                      </button>
                    )}
                  </td>
                  <td>
                    <input
                      className="input num"
                      type="number"
                      min="0"
                      step="any"
                      value={line.debit}
                      onChange={(e) =>
                        // إدخال قيمة في جانب يفرّغ الجانب الآخر: البند لا يكون الاثنين معًا.
                        patch(index, {
                          debit: e.target.value,
                          credit: e.target.value ? '' : line.credit,
                        })
                      }
                      aria-label="مدين"
                    />
                  </td>
                  <td>
                    <input
                      className="input num"
                      type="number"
                      min="0"
                      step="any"
                      value={line.credit}
                      onChange={(e) =>
                        patch(index, {
                          credit: e.target.value,
                          debit: e.target.value ? '' : line.debit,
                        })
                      }
                      aria-label="دائن"
                    />
                  </td>
                  <td>
                    {lines.length > 2 && (
                      <button
                        type="button"
                        className="btn btn-icon btn-ghost"
                        onClick={() => setLines(lines.filter((_, i) => i !== index))}
                        aria-label="حذف البند"
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        className="btn btn-sm mt-2"
        onClick={() => setLines([...lines, emptyLine()])}
      >
        <Icon name="plus" size={14} />
        إضافة بند
      </button>

      <div className="totals-box mt-4">
        <div className="totals-row">
          <span>مجموع المدين</span>
          <span className="num">{money(balance.totalDebit)}</span>
        </div>
        <div className="totals-row">
          <span>مجموع الدائن</span>
          <span className="num">{money(balance.totalCredit)}</span>
        </div>
        <div className="totals-row totals-final">
          <span>{balance.balanced ? 'القيد متوازن' : 'الفرق'}</span>
          <span
            className="num"
            style={{ color: balance.balanced ? 'var(--success)' : 'var(--danger)' }}
          >
            {balance.balanced ? '—' : money(Math.abs(balance.difference))}
          </span>
        </div>
      </div>

      {!balance.balanced && balance.errors.length > 0 && payload.length > 0 && (
        <div className="alert alert-warning mt-4">
          <Icon name="alert" size={18} />
          <div>{balance.errors[0]}</div>
        </div>
      )}

      {!editing && (
        <label className="row mt-4" style={{ cursor: 'pointer' }}>
          <input type="checkbox" checked={postNow} onChange={(e) => setPostNow(e.target.checked)} />
          <span className="small">
            ترحيل مباشر — القيد المرحَّل يؤثر في الأرصدة، وأي تعديل بعدها يُسجَّل في سجل التعديلات
          </span>
        </label>
      )}

      {/*
        سند القيد. القيد الجديد بلا معرّف بعد، فتُختار ملفاته وتُرفع بعد
        حفظه؛ والقائم يُرفع عليه مباشرةً ويُرى ما أُرفق قبل.
      */}
      {entry ? (
        <div className="mt-4">
          <Attachments entity="journalEntry" entityId={entry.id} title="سند القيد" />
        </div>
      ) : (
        <div className="form-grid mt-4">
          <PendingFiles
            id="j-files"
            files={files}
            onChange={setFiles}
            label="سند القيد (اختياري)"
            hint="إيصال، صورة تحويل، مستند — صورة أو PDF، يُرفق على القيد بعد حفظه."
          />
        </div>
      )}

      {pickingPartner !== null && (
        <Modal title="ربط البند بعميل" onClose={() => setPickingPartner(null)}>
          <AccountPicker
            onPick={(id, name) => {
              patch(pickingPartner, { partnerAccountId: id, partnerName: name });
              setPickingPartner(null);
            }}
          />
        </Modal>
      )}
    </Modal>
  );
}

function ReverseModal({
  entry,
  onClose,
  onDone,
}: {
  entry: Entry;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');

  const reverse = useMutation({
    mutationFn: () =>
      api(`/accounting/journal/${entry.id}/reverse`, { method: 'POST', body: { reason } }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={`عكس القيد ${entry.number}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            onClick={() => reverse.mutate()}
            disabled={reason.trim().length < 2 || reverse.isPending}
          >
            {reverse.isPending ? 'جارٍ العكس' : 'تأكيد العكس'}
          </button>
        </>
      }
    >
      {reverse.isError && (
        <div className="alert alert-danger mb-4">
          {reverse.error instanceof Error ? reverse.error.message : 'تعذّر العكس.'}
        </div>
      )}

      <p className="small mb-4">
        سيُنشأ قيد مضاد بنفس المبالغ في الاتجاه المعاكس. القيد الأصلي يبقى في الدفتر، فيظل
        المسار قابلًا للتدقيق.
      </p>

      <div className="field">
        <label className="label req" htmlFor="r-reason">
          سبب العكس
        </label>
        <textarea
          id="r-reason"
          className="textarea"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="خطأ في الحساب المدين، تكرار الإدخال، تصحيح مبلغ..."
          autoFocus
        />
      </div>
    </Modal>
  );
}

/**
 * إلغاء قيد مرحَّل بسبب.
 *
 * يُشرح الفرق عن العكس في سطرين: صاحب الدفتر يختار بينهما، والفرق ليس
 * بديهيًا — العكس يضيف قيدًا والإلغاء يُخرج القيد نفسه من الحسابات.
 */
function VoidModal({
  entry,
  onClose,
  onDone,
}: {
  entry: Entry;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');

  const cancel = useMutation({
    mutationFn: () =>
      api(`/accounting/journal/${entry.id}/void`, { method: 'POST', body: { reason } }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={`إلغاء القيد ${entry.number}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            تراجع
          </button>
          <button
            className="btn btn-danger"
            onClick={() => cancel.mutate()}
            disabled={reason.trim().length < 2 || cancel.isPending}
          >
            {cancel.isPending ? 'جارٍ الإلغاء' : 'تأكيد الإلغاء'}
          </button>
        </>
      }
    >
      {cancel.isError && (
        <div className="alert alert-danger mb-4">
          {cancel.error instanceof Error ? cancel.error.message : 'تعذّر الإلغاء.'}
        </div>
      )}

      <div className="small mb-4">
        <p>
          <strong>عكس</strong> = قيد جديد معاكس، ويبقى القيدان ظاهرين في الدفتر.
        </p>
        <p>
          <strong>إلغاء</strong> = القيد يخرج من الحسابات والتقارير، ويبقى ظاهرًا بحالة «ملغى»
          مع سببه.
        </p>
      </div>

      {entry.reversedBy && (
        <div className="alert alert-info mb-4">
          <Icon name="info" size={18} />
          <div>
            هذا القيد معكوس بالقيد {entry.reversedBy.number}، فيُلغى العاكس معه — صافي أثرهما على
            الأرصدة صفر في الحالتين.
          </div>
        </div>
      )}
      {entry.reversalOf && (
        <div className="alert alert-info mb-4">
          <Icon name="info" size={18} />
          <div>
            هذا قيد عاكس للقيد {entry.reversalOf.number}. إلغاؤه يعيد أثر ذلك القيد في الأرصدة،
            ويصير قابلًا للتعديل والعكس والإلغاء من جديد.
          </div>
        </div>
      )}

      <div className="field">
        <label className="label req" htmlFor="v-reason">
          سبب الإلغاء
        </label>
        <textarea
          id="v-reason"
          className="textarea"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="قيد مستورد خطأً، سيُسجَّل التحصيل بسند بتاريخه الصحيح..."
          autoFocus
        />
      </div>
    </Modal>
  );
}
